import React, { useState } from 'react';
import { useMetadata } from '../../context/MetadataContext'; 
import { getValue } from '../../utils/getValue';
import { useGameState } from '../../context/GameStateContext';
import { useGlobalError } from '../../context/GlobalErrorContext';
import CountryFlag from '../../utils/CountryFlag';
import FormattedTime from '../../utils/FormattedTime';
import { 
  ArrowLeft,       // Back
  Play             // Start
} from 'lucide-react';



const DuelPuzzleInfo = () => {
  const [loading, setLoading] = useState(false);
  const { getText } = useMetadata();
  const { gameData, rejectPuzzle, startPuzzle } = useGameState();
  const { triggerError } = useGlobalError();

  if (!gameData?.state) {
    triggerError({
      type: 'non-critical',
      message: 'DuelPuzzleInfo component rendered without puzzle data. This indicates a routing/status determination error.'
    });
    return null;
  }
  
  const handleRejectPuzzle = () => {
    rejectPuzzle();
  };

  const handleStartPuzzle = () => {
    startPuzzle();
  }
  
  return (
    <div className="dataContainer">
      <div className="containerHeader">
        <h3>{getValue(gameData, 'messages.pre_play')}</h3>
      </div>
      
      <div className="dataContent">
        {/* Puzzle Details */}
        <div className="contentGroup">
          <div className="dataRow">
            <span className="dataLabel">{getText('common_text.style')}:</span>
            <span className="dataValue">
              {(() => {
                const v = getValue(gameData, 'puzzle.style');
                if (!v) return '';
                const displayName = v.charAt(0).toUpperCase() + v.slice(1);
                return (
                  <a
                    href={`https://gridrank.games/guides/${v.toLowerCase()}.html`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {displayName}
                  </a>
                );
              })()}
            </span>
          </div>
          
          <div className="dataRow">
            <span className="dataLabel">{getText('common_text.size')}:</span>
            <span className="dataValue">{getValue(gameData, 'puzzle.size')}</span>
          </div>
          
          <div className="dataRow">
            <span className="dataLabel">{getText('common_text.difficulty')}:</span>
            <span className="dataValue">{getValue(gameData, 'puzzle.difficulty')}</span>
          </div>
        </div>
        
        {/* Opponent Details */}
        <div className="contentGroup">
          <div className="dataRow">
            <span className="dataLabel">{getText('common_text.opponent')}:</span>
            <span className="dataValue">{getValue(gameData, 'stats.opponent_nickname')}
              <CountryFlag countryCode={getValue(gameData, 'stats.opponent_country_code', '')} />
            </span>
          </div>
          
          
          {getValue(gameData, 'stats.opponent_birth_year', 0) > 0 && (
              <div className="dataRow">
            <span className="dataLabel">{getText('common_text.age_group')}:</span>
            <span className="dataValue">{getValue(gameData, 'stats.opponent_age_group')}
            </span>
          </div>
          )}

          {getValue(gameData, 'stats.opponent_gender', '') != '' && (
              <div className="dataRow">
            <span className="dataLabel">{getText('common_text.gender')}:</span>
            <span className="dataValue">{getValue(gameData, 'stats.opponent_gender')}
            </span>
          </div>
          )}

          <div className="dataRow">
            <span className="dataLabel">{getText('common_text.global_rank')}:</span>
            <span className="dataValue">{getValue(gameData, 'stats.opponent_global_puzzle_rank', 0)}
              &nbsp;/&nbsp;{getValue(gameData, 'stats.global_attempts')}
            </span>
          </div>
          
          <div className="dataRow">
            <span className="dataLabel">{getText('common_text.total_time')}:</span>
            <span className="dataValue">
              <FormattedTime seconds={getValue(gameData, 'stats.opponent_total_time')} />
            </span>
          </div>
          
          <div className="dataRow">
            <span className="dataLabel">{getText('common_text.moves')}:</span>
            <span className="dataValue">{getValue(gameData, 'stats.opponent_move_count', 'N/A')}</span>
          </div>
        </div>
        
      </div>
      
      <div className="actionButtonContainer">
        <button 
          className="iconButton secondaryButton" 
          onClick={handleRejectPuzzle}
          title={getText('common_text.back')}
          aria-label={getText('common_text.back')}
        >
          <ArrowLeft className="buttonIcon" />
        </button>
        
        <button 
          className="iconButton primaryButton" 
          onClick={handleStartPuzzle}
          disabled={loading}
          title={loading ? getText('common_text.loading') : getText('common_text.start')}
          aria-label={loading ? getText('common_text.loading') : getText('common_text.start')}
        >
          <Play className="buttonIcon" />
        </button>
      </div>
    </div>
  );
};

export default DuelPuzzleInfo;
